/**
 * Logo oficial da Essência: E estilizado em forma de planta/broto verde.
 * Único logo permitido — não usar círculo com 'E' nem outros símbolos.
 *
 * IMPORTANTE: não passar CSS custom properties (var(--x)) como `color` —
 * SVG presentation attributes não suportam var() em todos os browsers mobile.
 * Usar sempre valores hex diretos: "#2D4A3E" ou "#FFFFFF".
 */
import { useId } from 'react';

interface EssenciaLogoProps {
  className?: string;
  /** Cor do logo. Default: #2D4A3E. Use "#FFFFFF" no footer (fundo verde).
   *  ⚠️ Não usar var(--css-variable) — não funciona em SVG em iOS/Android. */
  color?: string;
  /** Tamanho em pixels (lado do quadrado). Default: 40 */
  size?: number;
}

const DEFAULT_COLOR = '#2D4A3E';

export function EssenciaLogo({ className = '', color = DEFAULT_COLOR, size = 40 }: EssenciaLogoProps) {
  // useId gera ID único por instância → evita colisão de gradientId
  // quando header + footer + sidebar renderizam na mesma página
  const uid = useId().replace(/:/g, '');
  const gradientId = `essencia-grad-${uid}`;
  const colorStop2 = color === DEFAULT_COLOR ? '#4A6B5D' : color;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} />
          <stop offset="100%" stopColor={colorStop2} />
        </linearGradient>
      </defs>

      {/* Folha Superior - Curvas Refinadas */}
      <path
        d="M20 4C20 4 11 8 11 16.5C11 21.5 15 25 20 25C25 25 29 21.5 29 16.5C29 8 20 4 20 4Z"
        fill={`url(#${gradientId})`}
      />

      {/* Folha Inferior / Broto - Alinhamento Perfeito */}
      <path
        d="M20 25C20 25 15.5 27 15.5 31C15.5 33.5 17.5 35 20 35C22.5 35 24.5 33.5 24.5 31C24.5 27 20 25 20 25Z"
        fill={`url(#${gradientId})`}
        fillOpacity="0.85"
      />

      {/* Caule - Traço Elegante */}
      <path
        d="M20 15V36"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
